import type { AutomationPendingRun } from '@/api/automationApi';
import { entityNoun } from '@/components/automation/automationTypes';
import {
  pendingRunNamePreview,
  pendingRunValueText,
} from '@/components/automation/pendingRunDisplay';
import { Table, TableWrap, TableHeader, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { formatActionType } from '@/utils/automationExecutionChange';
import { cn } from '@/utils/cn';

function ruleLabel(ruleName: string | null | undefined, ruleId: string): string {
  return ruleName || ruleId.slice(0, 8);
}

function statusBadgeClass(status: string): string {
  if (status === 'pending') {
    return 'border-warning/20 bg-warning-subtle text-warning-ink';
  }
  if (status === 'failed' || status === 'cancelled' || status === 'expired') {
    return 'border-error/20 bg-error-subtle text-error-ink';
  }
  return 'border-border bg-surface-muted text-foreground-muted';
}

export function PendingRunsTable({
  runs,
  canExecute,
  busy,
  onOpen,
  onConfirm,
  onCancel,
}: {
  runs: AutomationPendingRun[];
  canExecute: boolean;
  busy: boolean;
  onOpen: (run: AutomationPendingRun) => void;
  onConfirm: (run: AutomationPendingRun) => void;
  onCancel: (run: AutomationPendingRun) => void;
}) {
  return (
    <TableWrap>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Rule</TableHead>
            <TableHead>Entities</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Before</TableHead>
            <TableHead>After</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <tbody>
          {runs.map((run) => {
            const noun = run.entityNoun || entityNoun(run.entityType);
            const isPlacement = run.entityType === 'placement';
            return (
              <TableRow
                key={`${run.ruleId}:${run.scheduledSlot ?? ''}`}
                className="cursor-pointer hover:bg-surface-muted/60"
                onClick={() => onOpen(run)}
              >
                <TableCell className="font-medium">{ruleLabel(run.ruleName, run.ruleId)}</TableCell>
                <TableCell>
                  <div className="font-medium">
                    {run.total} {noun}
                  </div>
                  <div className="max-w-md truncate text-xs text-foreground-muted">
                    {pendingRunNamePreview(run.previewNames, run.total)}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {run.scheduledSlot ? new Date(run.scheduledSlot).toLocaleString() : '—'}
                </TableCell>
                <TableCell className="text-xs">{formatActionType(run.actionType)}</TableCell>
                <TableCell
                  className={
                    isPlacement
                      ? 'min-w-[14rem] max-w-xs whitespace-normal text-xs'
                      : 'max-w-[160px] truncate text-xs'
                  }
                >
                  {pendingRunValueText(run.beforeValue, run.total)}
                </TableCell>
                <TableCell
                  className={
                    isPlacement
                      ? 'min-w-[14rem] max-w-xs whitespace-normal text-xs'
                      : 'max-w-[160px] truncate text-xs'
                  }
                >
                  {pendingRunValueText(run.afterValue, run.total)}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-xs',
                      statusBadgeClass(run.status)
                    )}
                  >
                    {run.status}
                  </span>
                </TableCell>
                <TableCell>
                  {run.status === 'pending' && run.total > 0 && canExecute && (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="text-sm text-brand disabled:opacity-50"
                        disabled={busy}
                        onClick={() => onConfirm(run)}
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        className="text-sm text-foreground-muted disabled:opacity-50"
                        disabled={busy}
                        onClick={() => onCancel(run)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {runs.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="py-8 text-center text-foreground-muted">
                No pending logs
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
}
